import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookOpen, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupplierCodeConsentModal() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.must_change_password || user.role !== "supplier") return;
    let active = true;
    api.get("/supplier-code-of-conduct/my-status")
      .then((r) => { if (active) setStatus(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [user]);

  const show = status?.required && !status?.accepted && status?.current_document;
  if (!show) return null;

  const doc = status.current_document;
  const pdfSrc = `${BACKEND_URL}${doc.pdf_url}`;
  const isUpdate = status.needs_update;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/supplier-code-of-conduct/accept", { code_document_id: doc.id });
      toast.success("Código de Conducta aceptado, gracias.");
      setStatus({ ...status, accepted: true });
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar la aceptación.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
      data-testid="supplier-code-consent-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="supplier-code-consent-modal"
        className="bg-white rounded-2xl border border-slate-200 w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
      >
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFFBE6] border border-[#FFDF00] text-[#1A1A1A] flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-base text-[#1A1A1A]">
              {isUpdate
                ? "Actualizamos nuestro Código de Conducta para Proveedores"
                : "Código de Conducta para Proveedores"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Versión {doc.version_number} · vigente desde {doc.effective_date}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden p-5 space-y-4 flex flex-col">
          {isUpdate && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              Actualizamos nuestro Código de Conducta para Proveedores. Necesitamos que aceptes la nueva versión para continuar.
            </p>
          )}
          {!isUpdate && (
            <p className="text-sm text-slate-600 leading-relaxed">
              Para operar con Chizy Chiz, necesitamos que leas y aceptes nuestro Código de Conducta para Proveedores.
            </p>
          )}
          <object
            data={pdfSrc}
            type="application/pdf"
            data-testid="supplier-code-consent-pdf"
            className="flex-1 w-full min-h-[280px] rounded-xl border border-slate-200 bg-slate-50"
          >
            <div className="p-6 text-sm text-slate-600 text-center">
              Tu navegador no puede mostrar el PDF aquí. Usa el botón de descarga para abrirlo.
            </div>
          </object>
          <a
            href={pdfSrc}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="supplier-code-consent-download"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A1A1A] hover:underline self-start"
          >
            <Download className="w-4 h-4" /> Descargar PDF
          </a>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              data-testid="supplier-code-consent-check"
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#FFDF00]"
            />
            <span className="text-sm text-slate-700">
              He leído y acepto el Código de Conducta para Proveedores de Chizy Chiz (versión {doc.version_number}, vigente desde {doc.effective_date}).
            </span>
          </label>
        </div>

        <div className="p-5 border-t border-slate-100 flex items-center justify-end">
          <button
            data-testid="supplier-code-consent-accept-btn"
            onClick={accept}
            disabled={!checked || busy}
            className="btn-primary-chizy px-6 py-2.5 rounded-xl text-sm disabled:opacity-50"
          >
            {busy ? "Registrando..." : "Aceptar y continuar"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
